import { compactTranscriptFingerprint } from "./transcriptVersion";

function normalizeText(value = "") {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function normalizeStatus(value = "") {
  return normalizeText(value).toLowerCase().replace(/[\s-]+/g, "_");
}

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
}

const ACTIVE_SUMMARY_PURPOSE = "active_validated_summary";
const ACTIVE_SUMMARY_MODE_AI = "ai_evidence_synthesis";
const ACTIVE_SUMMARY_MODE_BASELINE = "deterministic_grounded_baseline";
const ACTIVE_SUMMARY_MODE_SOURCES = Object.freeze({
  [ACTIVE_SUMMARY_MODE_AI]: "ai-evidence-synthesis",
  [ACTIVE_SUMMARY_MODE_BASELINE]: "deterministic-grounded-baseline",
});
const ACTIVE_SUMMARY_VALIDATION_REASONS = Object.freeze({
  [ACTIVE_SUMMARY_MODE_AI]: "content_grounded_by_semantic_critic",
  [ACTIVE_SUMMARY_MODE_BASELINE]:
    "content_grounded_in_completion_transcript",
});

function resolveProtectedActiveSummaryMode(accepted = {}, summary = {}) {
  const protectedMetadata = asObject(accepted);
  const diagnostics = asObject(protectedMetadata.generationDiagnostics);
  const generationMode = normalizeText(protectedMetadata.generationMode);
  const selectedSummaryMode = normalizeText(diagnostics.selectedSummaryMode);
  const explicitMode = Boolean(generationMode || selectedSummaryMode);

  // Protected ACTIVE summaries created before explicit mode diagnostics keep
  // their original provenance/content authority. Any explicitly declared mode
  // must use the current server-issued allowlist and agree with its protected
  // diagnostic selection.
  if (!explicitMode) {
    return {
      valid: true,
      mode: "",
      expectedValidationReason:
        "content_grounded_in_completion_transcript",
    };
  }
  if (
    !Object.prototype.hasOwnProperty.call(
      ACTIVE_SUMMARY_MODE_SOURCES,
      generationMode
    ) ||
    selectedSummaryMode !== generationMode ||
    normalizeText(protectedMetadata.summaryPurpose) !==
      ACTIVE_SUMMARY_PURPOSE ||
    normalizeText(summary.source) !==
      ACTIVE_SUMMARY_MODE_SOURCES[generationMode]
  ) {
    return { valid: false, mode: generationMode || selectedSummaryMode };
  }
  return {
    valid: true,
    mode: generationMode,
    expectedValidationReason:
      ACTIVE_SUMMARY_VALIDATION_REASONS[generationMode],
  };
}

function normalizeSummaryContentText(value = "") {
  return String(value || "")
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeSummaryContentList(values = [], maxItems = 8) {
  const seen = new Set();
  return (Array.isArray(values) ? values : [])
    .filter((value) => typeof value === "string")
    .map(normalizeSummaryContentText)
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, maxItems);
}

function summaryContentValues(summary = {}) {
  return [
    ["summary_text", normalizeSummaryContentText(summary.summaryText)],
    [
      "participant_objective",
      normalizeSummaryContentText(summary.participantObjective),
    ],
    ...normalizeSummaryContentList(summary.agreedActions).map((value) => [
      "agreed_action",
      value,
    ]),
    ...normalizeSummaryContentList(summary.completedActions).map((value) => [
      "completed_action",
      value,
    ]),
    ...normalizeSummaryContentList(summary.detectedThemes).map((value) => [
      "detected_theme",
      value,
    ]),
    [
      "suggested_next_step",
      normalizeSummaryContentText(summary.suggestedNextStep),
    ],
    [
      "next_follow_up_focus",
      normalizeSummaryContentText(summary.nextFollowUpFocus),
    ],
  ].filter(([, value]) => Boolean(value));
}

const SHA256_ROUND_CONSTANTS = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
  0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
  0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
  0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
  0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
  0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

function rotateRight(value, shift) {
  return (value >>> shift) | (value << (32 - shift));
}

function utf8Bytes(value = "") {
  const bytes = [];
  for (const character of String(value || "")) {
    const codePoint = character.codePointAt(0);
    if (codePoint <= 0x7f) {
      bytes.push(codePoint);
    } else if (codePoint <= 0x7ff) {
      bytes.push(
        0xc0 | (codePoint >>> 6),
        0x80 | (codePoint & 0x3f)
      );
    } else if (codePoint <= 0xffff) {
      bytes.push(
        0xe0 | (codePoint >>> 12),
        0x80 | ((codePoint >>> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >>> 18),
        0x80 | ((codePoint >>> 12) & 0x3f),
        0x80 | ((codePoint >>> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    }
  }
  return bytes;
}

function sha256(value = "") {
  const bytes = utf8Bytes(value);
  const bitLength = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  const highBitLength = Math.floor(bitLength / 0x100000000);
  const lowBitLength = bitLength >>> 0;
  for (let shift = 24; shift >= 0; shift -= 8) {
    bytes.push((highBitLength >>> shift) & 0xff);
  }
  for (let shift = 24; shift >= 0; shift -= 8) {
    bytes.push((lowBitLength >>> shift) & 0xff);
  }

  const hash = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ];
  const words = new Array(64).fill(0);
  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      const position = offset + index * 4;
      words[index] = (
        (bytes[position] << 24) |
        (bytes[position + 1] << 16) |
        (bytes[position + 2] << 8) |
        bytes[position + 3]
      ) >>> 0;
    }
    for (let index = 16; index < 64; index += 1) {
      const left = words[index - 15];
      const right = words[index - 2];
      const sigma0 = rotateRight(left, 7) ^ rotateRight(left, 18) ^
        (left >>> 3);
      const sigma1 = rotateRight(right, 17) ^ rotateRight(right, 19) ^
        (right >>> 10);
      words[index] = (
        words[index - 16] + sigma0 + words[index - 7] + sigma1
      ) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = hash;
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temp1 = (
        h + sum1 + choice + SHA256_ROUND_CONSTANTS[index] + words[index]
      ) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sum0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }
    hash[0] = (hash[0] + a) >>> 0;
    hash[1] = (hash[1] + b) >>> 0;
    hash[2] = (hash[2] + c) >>> 0;
    hash[3] = (hash[3] + d) >>> 0;
    hash[4] = (hash[4] + e) >>> 0;
    hash[5] = (hash[5] + f) >>> 0;
    hash[6] = (hash[6] + g) >>> 0;
    hash[7] = (hash[7] + h) >>> 0;
  }
  return hash.map((word) => word.toString(16).padStart(8, "0")).join("");
}

function summaryContentFingerprint(summary = {}) {
  const digest = sha256(JSON.stringify(summaryContentValues(summary))).slice(0, 32);
  return `summary-content:v1:${digest}`;
}

function toIso(value) {
  if (!value) return "";
  if (typeof value?.toDate === "function") {
    return value.toDate().toISOString();
  }
  if (typeof value?.toMillis === "function") {
    const millis = value.toMillis();
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString();
  }
  if (typeof value?.seconds === "number") {
    return new Date(value.seconds * 1000).toISOString();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString();
  }
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : "";
}

function hasExactBoundaryFields(value = {}, boundary = {}) {
  const candidate = asObject(value);
  return Boolean(
    normalizeText(candidate.orgId) === boundary.orgId &&
    normalizeText(candidate.userId) === boundary.userId &&
    normalizeText(candidate.sessionId) === boundary.sessionId &&
    normalizeText(candidate.completionVersionId) ===
      boundary.completionVersionId &&
    normalizeText(candidate.transcriptFingerprint) ===
      boundary.transcriptFingerprint &&
    Number(candidate.sourceMessageCount) === boundary.sourceMessageCount
  );
}

function invalid(reason, extra = {}) {
  return {
    available: false,
    completionValid: false,
    completionAuditValid: false,
    finalizationBindingValid: false,
    trustedFinalSummary: false,
    automaticEmailBindingValid: false,
    reminderBindingValid: false,
    manualAttemptBindingsValid: false,
    reason,
    ...extra,
  };
}

export function getTrustedActiveSummaryState(record = {}) {
  const source = asObject(record);
  const continuation = asObject(source.postSessionContinuation);
  const lifecycle = asObject(continuation.activeSummary);
  const accepted = asObject(lifecycle.accepted);
  const summary = asObject(source.sessionSummary);
  const provenance = asObject(accepted.generationProvenance);
  const validation = asObject(accepted.contentValidation);
  const messages = Array.isArray(source.messages) ? source.messages : [];
  const orgId = normalizeText(source.orgId);
  const userId = normalizeText(source.userId);
  const sessionId = normalizeText(source.id || source.sessionId || source.createdAt);
  const transcriptFingerprint = messages.length
    ? compactTranscriptFingerprint(messages)
    : "";
  const acceptedFingerprint = normalizeText(accepted.transcriptFingerprint);
  const acceptedCount = Number(accepted.sourceMessageCount);
  const lifecycleStatus = normalizeStatus(lifecycle.status);
  const summaryStatus = normalizeStatus(summary.status);
  const acceptedSummaryFingerprint = normalizeText(
    accepted.summaryFingerprint
  );
  const projectedSummaryFingerprint = summaryContentFingerprint(summary);
  const protectedMode = resolveProtectedActiveSummaryMode(accepted, summary);
  const exact = Boolean(
    transcriptFingerprint &&
    transcriptFingerprint === acceptedFingerprint &&
    messages.length === acceptedCount
  );
  const exactScope = (candidate = {}) => Boolean(
    normalizeText(candidate.orgId) === orgId &&
    normalizeText(candidate.userId) === userId &&
    normalizeText(candidate.sessionId) === sessionId &&
    normalizeText(candidate.transcriptFingerprint) === acceptedFingerprint &&
    Number(candidate.sourceMessageCount) === acceptedCount &&
    normalizeText(candidate.evidenceSignature) ===
      normalizeText(accepted.evidenceSignature) &&
    normalizeText(candidate.activeSummaryVersion) ===
      normalizeText(accepted.activeSummaryVersion)
  );
  const expectedProvenanceKeys = [
    "purpose",
    "version",
    "operationId",
    "attemptId",
    "orgId",
    "userId",
    "sessionId",
    "transcriptFingerprint",
    "sourceMessageCount",
    "evidenceSignature",
    "activeSummaryVersion",
  ];
  const provenanceValid = Boolean(
    Object.keys(provenance).sort().join("|") ===
      expectedProvenanceKeys.sort().join("|") &&
    provenance.purpose === "active_validated_summary" &&
    provenance.version === "active-summary:v1" &&
    normalizeText(provenance.operationId) ===
      normalizeText(accepted.operationId) &&
    normalizeText(provenance.attemptId) ===
      normalizeText(accepted.attemptId) &&
    exactScope(provenance)
  );
  const acceptedValid = Boolean(
    accepted.purpose === "active_validated_summary" &&
    accepted.version === "active-summary:v1" &&
    normalizeText(accepted.operationId) &&
    normalizeText(accepted.attemptId) &&
    exactScope(accepted)
  );
  const contentValidationValid = Boolean(
    validation.status === "accepted" &&
    validation.reason === protectedMode.expectedValidationReason &&
    validation.purpose === ACTIVE_SUMMARY_PURPOSE &&
    validation.version === "active-summary:v1" &&
    normalizeText(validation.completionVersionId) === "" &&
    normalizeText(validation.contentFingerprint) ===
      acceptedSummaryFingerprint &&
    acceptedSummaryFingerprint === projectedSummaryFingerprint &&
    normalizeText(validation.transcriptFingerprint) === acceptedFingerprint &&
    Number(validation.sourceMessageCount) === acceptedCount &&
    normalizeText(validation.evidenceSignature) ===
      normalizeText(accepted.evidenceSignature) &&
    normalizeText(validation.activeSummaryVersion) ===
      normalizeText(accepted.activeSummaryVersion)
  );
  const snapshotChecks = [
    [source.sessionComplete !== true, "session_already_complete"],
    [Boolean(orgId && userId && sessionId), "missing_session_scope"],
    [
      ["current", "stale", "generating", "failed"].includes(lifecycleStatus),
      "active_lifecycle_status_invalid",
    ],
    [Boolean(normalizeText(summary.summaryText)), "active_summary_text_missing"],
    [
      summary.generationStatus === "active_validated",
      "active_generation_status_invalid",
    ],
    [
      ["current", "stale"].includes(summaryStatus),
      "active_summary_status_invalid",
    ],
    [
      (summaryStatus === "current" && summary.isCurrent === true) ||
        (summaryStatus === "stale" && summary.isCurrent === false),
      "active_summary_current_flag_invalid",
    ],
    [summary.canonical === false, "active_summary_canonical_invalid"],
    [summary.fallbackUsed === false, "active_summary_fallback_invalid"],
    [Boolean(normalizeText(summary.source)), "active_summary_source_missing"],
    [
      !/(?:fallback|placeholder|draft|unsupported)/i.test(summary.source),
      "active_summary_source_untrusted",
    ],
    [normalizeText(summary.orgId) === orgId, "active_summary_org_mismatch"],
    [normalizeText(summary.userId) === userId, "active_summary_user_mismatch"],
    [
      normalizeText(summary.sessionId) === sessionId,
      "active_summary_session_mismatch",
    ],
    [
      normalizeText(summary.sourceTranscriptFingerprint) === acceptedFingerprint,
      "active_summary_transcript_fingerprint_mismatch",
    ],
    [
      Number(summary.sourceMessageCount) === acceptedCount,
      "active_summary_message_count_mismatch",
    ],
    [acceptedValid, "active_accepted_metadata_invalid"],
    [provenanceValid, "active_generation_provenance_invalid"],
    [protectedMode.valid, "active_generation_mode_invalid"],
    [contentValidationValid, "active_content_validation_invalid"],
  ];
  const rejectionReason = snapshotChecks.find(([valid]) => !valid)?.[1] || "";
  const snapshotValid = !rejectionReason;
  if (snapshotValid) {
    if (exact && lifecycleStatus === "current") {
      return {
        state: "current",
        snapshotValid: true,
        exact: true,
        reason: "trusted_active_summary",
        selectedSummaryMode: protectedMode.mode,
        summarySource: normalizeText(summary.source),
        summary,
      };
    }
    return {
      state: lifecycleStatus === "generating" ? "updating" : "stale",
      snapshotValid: true,
      exact: false,
      reason: "trusted_earlier_active_summary",
      selectedSummaryMode: protectedMode.mode,
      summarySource: normalizeText(summary.source),
      summary,
    };
  }
  if (lifecycleStatus === "failed") {
    return {
      state: "failed",
      snapshotValid: false,
      exact: false,
      reason: rejectionReason,
      selectedSummaryMode: protectedMode.mode,
      summarySource: normalizeText(summary.source),
      summary: null,
    };
  }
  if (lifecycleStatus === "generating") {
    return {
      state: "updating",
      snapshotValid: false,
      exact: false,
      reason: rejectionReason,
      selectedSummaryMode: protectedMode.mode,
      summarySource: normalizeText(summary.source),
      summary: null,
    };
  }
  return {
    state: "not_ready",
    snapshotValid: false,
    exact: false,
    reason: rejectionReason,
    selectedSummaryMode: protectedMode.mode,
    summarySource: normalizeText(summary.source),
    summary: null,
  };
}

export function hasTrustedActiveSummaryProvenance(record = {}) {
  return getTrustedActiveSummaryState(record).snapshotValid;
}

export function validatePostSessionProvenance(record = {}) {
  const source = asObject(record);
  const continuation = asObject(source.postSessionContinuation);
  const completionVersion = asObject(source.completionVersion);
  const completionAudit = asObject(continuation.completion);
  const finalization = asObject(continuation.finalization);
  const summary = asObject(source.sessionSummary);
  const automaticEmail = asObject(continuation.automaticEmail);
  const reminder = asObject(continuation.reminder);
  const messages = Array.isArray(source.messages) ? source.messages : [];
  const orgId = normalizeText(source.orgId);
  const userId = normalizeText(source.userId);
  const sessionId = normalizeText(
    source.id || source.sessionId || source.createdAt
  );

  if (!orgId || !userId || !sessionId) {
    return invalid("missing_session_scope", {
      available: Object.keys(continuation).length > 0,
    });
  }
  if (source.sessionComplete !== true || !Object.keys(completionVersion).length) {
    return invalid("missing_versioned_completion", {
      available: Object.keys(continuation).length > 0,
    });
  }
  if (!messages.length) {
    return invalid("missing_authoritative_transcript", {
      available: Object.keys(continuation).length > 0,
    });
  }

  const transcriptFingerprint = compactTranscriptFingerprint(messages);
  const sourceMessageCount = messages.length;
  const userTurnCount = messages.filter((message) => {
    const role = normalizeText(message?.role || message?.sender).toLowerCase();
    return role !== "assistant" && role !== "nia" &&
      normalizeText(message?.content || message?.text);
  }).length;
  const completedAt = toIso(completionVersion.completedAt);
  const endedAt = toIso(source.endedAt);
  const completionVersionId = normalizeText(completionVersion.id);
  const expectedCompletionVersionId = completedAt
    ? `completion:v1:${transcriptFingerprint}:${completedAt}`
    : "";
  const declaredMessageCount = Number(source.messageCount);
  const completionValid = Boolean(
    completedAt &&
    completedAt === endedAt &&
    completionVersionId === expectedCompletionVersionId &&
    normalizeText(completionVersion.orgId) === orgId &&
    normalizeText(completionVersion.userId) === userId &&
    normalizeText(completionVersion.sessionId) === sessionId &&
    normalizeText(completionVersion.transcriptFingerprint) ===
      transcriptFingerprint &&
    Number(completionVersion.sourceMessageCount) === sourceMessageCount &&
    sourceMessageCount >= 5 &&
    userTurnCount >= 2 &&
    (
      !Number.isFinite(declaredMessageCount) ||
      declaredMessageCount === sourceMessageCount
    )
  );
  const boundary = {
    orgId,
    userId,
    sessionId,
    completionVersionId,
    transcriptFingerprint,
    sourceMessageCount,
    completedAt,
  };
  if (!Object.keys(continuation).length) {
    return invalid("missing_post_session_continuation", {
      completionValid,
      boundary,
    });
  }
  const completionAuditValid = Boolean(
    completionValid &&
    hasExactBoundaryFields(completionAudit, boundary) &&
    toIso(completionAudit.completedAt) === completedAt
  );
  const finalizationStatus = normalizeStatus(finalization.status);
  const summaryVersion = normalizeText(finalization.summaryVersion);
  const finalizationBindingValid = Boolean(
    completionAuditValid &&
    ["pending", "succeeded", "reused", "failed"].includes(finalizationStatus) &&
    normalizeText(finalization.purpose) === "final_canonical_summary" &&
    hasExactBoundaryFields(finalization, boundary) &&
    summaryVersion
  );
  const summaryStatus = normalizeStatus(
    summary.generationStatus || summary.status
  );
  const summarySource = normalizeText(summary.source);
  const agreedActionsShapeValid = Boolean(
    Array.isArray(summary.agreedActions) &&
    summary.agreedActions.every((action) => normalizeText(action))
  );
  const completedActionsShapeValid = Boolean(
    Array.isArray(summary.completedActions) &&
    summary.completedActions.every((action) => normalizeText(action))
  );
  const completedActions = completedActionsShapeValid
    ? summary.completedActions.map(normalizeText)
    : [];
  const completedActionEvidenceValid = Boolean(
    completedActionsShapeValid &&
    (
      completionAudit.completedActionConfirmed === true
        ? completedActions.length > 0
        : completedActions.length === 0
    )
  );
  const summaryStructuredShapeValid = Boolean(
    typeof summary.participantObjective === "string" &&
    agreedActionsShapeValid &&
    completedActionEvidenceValid
  );
  const trustedFinalSummary = Boolean(
    finalizationBindingValid &&
    ["succeeded", "reused"].includes(finalizationStatus) &&
    normalizeText(summary.summaryText) &&
    ["final", "reviewed"].includes(summaryStatus) &&
    summary.canonical === true &&
    summary.fallbackUsed === false &&
    summary.isCurrent !== false &&
    normalizeStatus(summary.persistenceStatus) === "persisted" &&
    summaryStructuredShapeValid &&
    summarySource &&
    !/(?:fallback|placeholder|low-context)/i.test(summarySource) &&
    normalizeText(summary.orgId) === orgId &&
    normalizeText(summary.userId) === userId &&
    normalizeText(summary.sessionId) === sessionId &&
    normalizeText(summary.completionVersionId) === completionVersionId &&
    normalizeText(summary.summaryVersion) === summaryVersion &&
    normalizeText(summary.sourceTranscriptFingerprint) ===
      transcriptFingerprint &&
    Number(summary.sourceMessageCount) === sourceMessageCount
  );
  const automaticEmailBindingValid = Boolean(
    trustedFinalSummary &&
    automaticEmail.purpose === "automatic_initial_summary" &&
    hasExactBoundaryFields(automaticEmail, boundary) &&
    normalizeText(automaticEmail.summaryVersion) === summaryVersion
  );
  const reminderBindingValid = Boolean(
    trustedFinalSummary &&
    reminder.purpose === "no_return_reminder" &&
    hasExactBoundaryFields(reminder, boundary) &&
    normalizeText(reminder.summaryVersion) === summaryVersion
  );
  const manualAttempts = Array.isArray(continuation?.manualResend?.attempts)
    ? continuation.manualResend.attempts
    : [];
  const manualAttemptBindingsValid = Boolean(
    manualAttempts.length === 0 ||
    (
      trustedFinalSummary &&
      manualAttempts.every((attempt) => (
        asObject(attempt).purpose === "manual_summary_resend" &&
        hasExactBoundaryFields(attempt, boundary) &&
        normalizeText(attempt.summaryVersion) === summaryVersion &&
        normalizeText(attempt.attemptId)
      ))
    )
  );

  let reason = "trusted";
  if (!completionValid) reason = "completion_provenance_mismatch";
  else if (!completionAuditValid) reason = "completion_audit_mismatch";
  else if (!finalizationBindingValid) reason = "finalization_provenance_mismatch";
  else if (
    ["succeeded", "reused"].includes(finalizationStatus) &&
    !trustedFinalSummary
  ) {
    reason = "final_summary_provenance_mismatch";
  } else if (finalizationStatus === "pending") reason = "finalization_pending";
  else if (finalizationStatus === "failed") reason = "finalization_failed";

  return {
    available: true,
    completionValid,
    completionAuditValid,
    finalizationBindingValid,
    trustedFinalSummary,
    automaticEmailBindingValid,
    reminderBindingValid,
    manualAttemptBindingsValid,
    reason,
    boundary,
    summaryVersion,
  };
}

export function hasTrustedFinalSummaryProvenance(record = {}) {
  return validatePostSessionProvenance(record).trustedFinalSummary;
}
